import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { SEMANTIC_ROLES } from '../../../../permissions/roles.js';
import { Person } from '../../../../users/entities/person.js';
import { UserListAPI } from '../../../../users/list/api/UserListAPI.js';

const AddGroupMemberDialog = stateful(
  (props: {
    groupId: Maybe<string>;
    onAddUser: (userIDs: string) => Promise<void>;
    resolve: () => void;
    reject: () => void;
  }) => {
    const [filter, setFilter] = useState(new AdornisFilter({}));
    const [onlyActive, setOnlyActive] = useState(true);
    const [metaRole, setMetaRole] = useState<string | null>(null);

    useEffect(() => {
      const rolesPicked = metaRole
        ? onlyActive
          ? // roles picked and active -> use semantic role active array
            SEMANTIC_ROLES.find(r => r.label === metaRole)?.activeRole
          : // roles picked but not onlyActive -> use value to use all roles of the semantic group
            SEMANTIC_ROLES.find(r => r.label === metaRole)?.value
        : onlyActive
          ? // no roles picked -> all active roles
            SEMANTIC_ROLES.flatMap(r => r.activeRole)
          : // no roles picked and no onlyActive -> no filter
            null;

      /**
       * Nothing picked: search stays consistent but remove use a new empty filter otherwise.
       */
      if (!rolesPicked?.length) {
        setFilter(new AdornisFilter({ search: filter.search }));
        return;
      }

      /**
       * Build or string aspect so we search for all roles selected in the case of multiple
       */
      const rolesAspect = new StringAspect({
        fieldName: 'roles.name',
        mode: 'containsExactOR',
        value: rolesPicked.filter(isNotNullish)?.join(','),
      });

      setFilter(new AdornisFilter({ search: filter.search, aspects: [rolesAspect] }));
    }, [onlyActive, metaRole]);

    /**
     * Autofocus the input
     */
    useEffect(() => {
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLInputElement>('.sg-add-group-member-dialog input');
        input?.focus();
      });
    }, []);

    return html`
      <div class="dialog-container sg-add-group-member-dialog">
        ${DialogHeader({
          title: 'Mitglied hinzufügen',
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <div class="flex flex-col gap-4">
          <div class="flex flex-wrap items-center gap-4 rounded-box bg-base-200 p-3">
            <div class="flex flex-col gap-1">
              ${ToggleInput({
                value: onlyActive,
                label: 'Nur aktive Rollen',
                onPicked: value => setOnlyActive(value ?? false),
              })}
            </div>
            <div class="flex flex-1 flex-col gap-1 min-w-[200px]">
              ${DropdownSelectionInput<string>({
                placeholder: 'Rolle',
                placeholderMode: 'prefix',
                clearable: true,
                selectables: SEMANTIC_ROLES.map(r => r.label),
                onPicked: value => setMetaRole(value),
              })}
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-semibold text-base-content/70">Benutzer auswählen</span>
            ${EntityPickerInput({
              placeholder: 'Benutzer suchen',
              class: Person,
              operation: UserListAPI.subscribe,
              filter,
              renderItem: entity => {
                const roleNames = Array.from(new Set(entity.roles?.map(r => r.name).filter(Boolean) ?? []));
                return html`
                  <div class="flex flex-col justify-center gap-1 p-2">
                    <p>
                      <b>
                        ${entity.profilDaten?.stammdaten?.vorname ?? 'Kein Vorname'}
                        ${entity.profilDaten?.stammdaten?.nachname ?? 'Kein Nachname'}
                      </b>
                    </p>
                    <p>${entity.debitorennummer} - ${entity.email}</p>
                    <p class="text-sm text-base-content/70">
                      ${roleNames.length ? roleNames.join(', ') : 'Keine Rolle'}
                    </p>
                  </div>
                `;
              },
              selectionSet: {
                _id: 1,
                debitorennummer: 1,
                email: 1,
                profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
                roles: { name: 1 },
              },
              onPicked: async value => {
                if (!value?.length) return;
                await props.onAddUser(value);
                props.resolve();
              },
            })}
          </div>
        </div>
      </div>
    `;
  },
);

export async function openAddGroupMemberDialog(groupId: Maybe<string>, onAddUser: (userIDs: string) => Promise<void>) {
  return showDialog<void>({
    content: (resolve, reject) => AddGroupMemberDialog({ groupId, onAddUser, resolve, reject }),
    modalBoxClasses: 'max-w-2xl',
  });
}
